import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { eventToFormInitialData } from '../../utils/eventFormInitialData';
import { api, apiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import Button from '../../components/ui/Button';
import AttendanceOverview from '../../components/AttendanceOverview';
import EventStatusBadge, { type EventStatus } from '../../components/EventStatusBadge';
import EventStatusActions from '../../components/EventStatusActions';
import EventComments from '../../components/messaging/EventComments';
import ExportModal from '../../components/ExportModal';
import { getEventExportFields, ExportableData, ExportField } from '../../utils/exportUtils';
import { memberFields } from '../../utils/entityFields';
import EventForm, { IEventFormData } from '../../components/forms/EventForm';
import CollapsibleWidget from '../../components/CollapsibleWidget';
import RequirementsWidget from '../../components/RequirementsWidget';
import EventRequirementsConfig from '../../components/EventRequirementsConfig';
import RoleAccessWidget from '../../components/RoleAccessWidget';
import { translateError } from '../../utils/errorMessages';

interface IEvent {
  _id: string;
  name: string;
  description: string;
  location?: string | null;
  startTime: string;
  endTime: string;
  template?: string;
  defaultAttendance: boolean | null;
  responsibleId?: number | null;
  responsible?: string | { _id: number; name: string | null } | null;
  members: (string | number)[];
  groups: (string | number)[];
  coveredByMembershipIds?: number[];
  price?: number;
  isOpen?: boolean;
  capacity?: number;
  publicSlug?: string;
  private?: boolean;
  extendedDescription?: string;
  gallery?: string[];
  colorSettings?: {
    primaryColor?: string;
    secondaryColor?: string;
  };
  status?: EventStatus;
}

interface MemberAttendance {
  _id: string;
  name: string;
  color: string;
  status: 'attending' | 'not-attending' | 'maybe';
  user?: {
    _id: string;
    name: string;
    email: string;
    admin?: boolean;
  };
  groups?: { _id: string; name: string }[];
}

interface GroupAttendance {
  _id: string;
  name: string;
  members: MemberAttendance[];
}

interface AttendanceData {
  eventId: string;
  defaultAttendance: boolean | null;
  groups: GroupAttendance[];
  directMembers: MemberAttendance[];
}

const EventEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<IEvent | null>(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'form' | 'attendance' | 'comments'>('form');

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportData, setExportData] = useState<ExportableData[]>([]);
  const [attendanceMap, setAttendanceMap] = useState<Map<string, boolean | null>>(new Map());
  const [exportLoading, setExportLoading] = useState(false);
  const [requirementExportFields, setRequirementExportFields] = useState<ExportField[]>([]);

  const navigate = useNavigate();

  const refetchEvent = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.events.get(id);
      setEvent(data as IEvent);
      setLoading(false);
    } catch (err: any) {
      setError(translateError(err, t));
      setLoading(false);
      if (err.response && err.response.status === 404) {
        navigate('/admin/events'); // Go back to list if event not found
      }
    }
  }, [id, navigate, t]);

  useEffect(() => {
    void refetchEvent();
  }, [refetchEvent]);

  const handleSubmit = async (data: IEventFormData) => {
    setSubmitting(true);
    setError(null);

    try {
      await api.events.update(id!, data);
      addToast(t('events.editTitle') + ' - ' + t('common.save') + '!', 'success');
      navigate('/admin/events');
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
      setSubmitting(false);
    }
  };

  const handleExportClick = async () => {
    if (!id) return;

    setExportLoading(true);
    try {
      // Fetch attendance data, full member data, and event requirements in parallel
      const [attendanceResponse, fullMembersResponse, eventRequirementsResponse] = await Promise.all([
        apiClient.get<AttendanceData>(`/api/events/${id}/attendance`),
        api.members.list(),
        apiClient.get<any[]>(`/api/requirements/events/${id}/requirements`),
      ]);

      const attendanceData = attendanceResponse.data;
      const fullMembersData = fullMembersResponse.data as any[];
      const eventRequirements = eventRequirementsResponse.data;

      // Create a map of member ID to full member data
      const memberDataMap = new Map(fullMembersData.map(m => [m._id, m]));

      // Collect all members from groups and direct members
      const allEventMembers: ExportableData[] = [];
      const attendanceStatusMap = new Map<string, boolean | null>();

      // Add members from groups
      for (const group of attendanceData.groups) {
        for (const member of group.members) {
          const fullMember = memberDataMap.get(member._id);
          if (fullMember && !allEventMembers.find(m => m._id === member._id)) {
            allEventMembers.push({ ...fullMember });
            const status = member.status === 'attending' ? true : member.status === 'not-attending' ? false : null;
            attendanceStatusMap.set(member._id, status);
          }
        }
      }

      // Add direct members
      for (const member of attendanceData.directMembers) {
        const fullMember = memberDataMap.get(member._id);
        if (fullMember && !allEventMembers.find(m => m._id === member._id)) {
          allEventMembers.push({ ...fullMember });
          const status = member.status === 'attending' ? true : member.status === 'not-attending' ? false : null;
          attendanceStatusMap.set(member._id, status);
        }
      }

      // Create requirement export fields and fetch member requirements
      const reqFields: ExportField[] = [];
      if (eventRequirements.length > 0) {
        // Fetch member requirements for all members in this event
        const memberIds = allEventMembers.map(m => m._id);
        interface MemberRequirement {
          requirementType: { _id: string } | string;
          status: string;
        }
        const memberRequirementsPromises = memberIds.map(memberId =>
          apiClient.get<MemberRequirement[]>(`/api/requirements/members/${memberId}/requirements?event=${id}`)
            .then(res => ({ memberId, requirements: res.data }))
            .catch((): { memberId: string; requirements: MemberRequirement[] } => ({ memberId, requirements: [] }))
        );
        const memberRequirementsResults = await Promise.all(memberRequirementsPromises);

        // Create a map of memberId -> requirementTypeId -> status
        const memberReqStatusMap = new Map<string, Map<string, string>>();
        for (const { memberId, requirements } of memberRequirementsResults) {
          const reqMap = new Map<string, string>();
          for (const req of requirements) {
            const reqType = req.requirementType;
            const typeId = typeof reqType === 'string' ? reqType : reqType._id;
            const status = req.status; // 'pending' | 'submitted' | 'approved' | 'rejected'
            reqMap.set(typeId, status);
          }
          memberReqStatusMap.set(memberId, reqMap);
        }

        // Add requirement fields and values to export data
        for (const eventReq of eventRequirements) {
          const reqType = eventReq.requirementType;
          const reqTypeId = reqType._id;
          const reqName = reqType.name;

          // Add export field for this requirement
          reqFields.push({
            key: `req_${reqTypeId}`,
            label: reqName,
            category: 'requirement',
            defaultChecked: false,
          });

          // Add requirement status to each member's export data
          for (const memberData of allEventMembers) {
            const memberId = memberData._id;
            const memberReqMap = memberReqStatusMap.get(memberId);
            const status = memberReqMap?.get(reqTypeId) || 'pending';

            // Convert status to display value
            let displayValue: string;
            switch (status) {
              case 'approved':
                displayValue = t('common.yes');
                break;
              case 'rejected':
                displayValue = t('common.no');
                break;
              case 'submitted':
                displayValue = t('requirements.status.submitted');
                break;
              default:
                displayValue = '-';
            }
            memberData[`req_${reqTypeId}`] = displayValue;
          }
        }
      }

      setRequirementExportFields(reqFields);
      setExportData(allEventMembers);
      setAttendanceMap(attendanceStatusMap);
      setShowExportModal(true);
    } catch (err: any) {
      console.error('Failed to fetch export data:', err);
    } finally {
      setExportLoading(false);
    }
  };

  // Generate export fields from entity definition (includes attendance + dynamic requirements)
  const exportFields = useMemo(() => {
    const baseFields = getEventExportFields(memberFields, ['name', 'user.name', 'groups', 'attendance']);
    return [...baseFields, ...requirementExportFields];
  }, [requirementExportFields]);

  if (loading && !event) return <p>{t('events.loadingEvent')}</p>;
  if (error && !event) return <p className="error-message">{t('common.error')}: {error}</p>;
  if (!event) return <p>{t('events.eventNotFound')}</p>;

  const initialData = eventToFormInitialData(event);

  return (
    <div className="admin-page-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>{t('events.editTitle')}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <EventStatusBadge status={event.status} />
          <EventStatusActions
            eventId={event._id}
            eventName={event.name}
            status={event.status}
            onChanged={refetchEvent}
            appearance="labelled"
          />
          <Button variant="secondary" size="sm" onClick={handleExportClick} disabled={exportLoading}>
            {exportLoading ? t('common.loading') : t('export.exportMembers')}
          </Button>
        </div>
      </div>
      {event.template && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
          {t('eventDetails.templateNote')}
        </p>
      )}

      {/* Mobile Tab Switcher */}
      <div className="tab-switcher mobile-only">
        <button
          className={activeTab === 'form' ? 'active' : ''}
          onClick={() => setActiveTab('form')}
        >
          {t('eventDetails.tabEdit')}
        </button>
        <button
          className={activeTab === 'attendance' ? 'active' : ''}
          onClick={() => setActiveTab('attendance')}
        >
          {t('eventDetails.tabAttendance')}
        </button>
        <button
          className={activeTab === 'comments' ? 'active' : ''}
          onClick={() => setActiveTab('comments')}
        >
          {t('eventDetails.tabComments')}
        </button>
      </div>

      <div className="edit-page-layout">
        <div className={`edit-page-form ${activeTab === 'form' ? 'active' : ''}`}>
          {error && <p className="error-message">{t('common.error')}: {error}</p>}
          <EventForm
            initialData={initialData}
            seedKey={event._id}
            onSubmit={handleSubmit}
            loading={submitting}
            submitButtonText={t('events.updateButton')}
            eventId={id}
          />
        </div>

        <div className={`edit-page-widgets ${activeTab === 'attendance' || activeTab === 'comments' ? 'active' : ''}`}>
          {id && (
            <>
              <CollapsibleWidget
                title={t('eventDetails.tabComments')}
                defaultExpanded={true}
                storageKey={`event-${id}-comments`}
              >
                <EventComments eventId={id} />
              </CollapsibleWidget>
              <CollapsibleWidget
                title={t('eventDetails.tabAttendance')}
                defaultExpanded={true}
                storageKey={`event-${id}-attendance`}
              >
                <AttendanceOverview
                  eventId={id}
                  eventName={event?.name}
                  authToken={JSON.parse(localStorage.getItem('userInfo') || '{}').token}
                  eventStatus={event?.status}
                />
              </CollapsibleWidget>
              <RequirementsWidget eventId={id} />
              <CollapsibleWidget
                title={t('requirements.configTitle')}
                defaultExpanded={false}
                storageKey={`event-${id}-requirements-config`}
              >
                <EventRequirementsConfig eventId={id} />
              </CollapsibleWidget>
              <CollapsibleWidget
                title={t('roles.entityAccess')}
                defaultExpanded={false}
                storageKey={`event-${id}-role-access`}
              >
                <RoleAccessWidget entityType="events" entityId={id} />
              </CollapsibleWidget>
            </>
          )}
        </div>
      </div>

      <Link to="/admin/events" className="form-link">{t('events.goBack')}</Link>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={exportData}
        filename={`event-${event.name.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}`}
        availableFields={exportFields}
        title={t('export.eventExportTitle')}
        attendanceMap={attendanceMap}
      />
    </div>
  );
};

export default EventEditPage;
